import React, { type ReactElement, type ReactNode } from "react";
import { cn } from "@ndd/ui";
import type { PatientListItem } from "../../../../shared/api-types";
import { THEMED_CARD, THEMED_DIVIDE, THEMED_ROW_HOVER } from "../../lib/always-look";
import { avatarClass, initials } from "../../lib/avatar";
import {
  lastVisitLabel,
  nextVisitLabel,
  patientName,
  type PatientListView,
} from "../../lib/patients-client";

// The RD's Patients list (NO-835). White rows on the cream page, a lime
// initials avatar, the name, then Last visit and Next visit. Every state is
// neutral text: an empty list is normal, never an alarm. A leaf module
// (`import React`, relative imports, no router, no queries) so `tsx --test`
// renders it; the page supplies the links through `renderPatientLink`. The
// patient page (NO-849) reuses the avatar.

export interface PatientLinkProps {
  patientId: string;
  className: string;
  dataAttributes: { "data-patient-id": string };
  children: ReactNode;
}

export type RenderPatientLink = (props: PatientLinkProps) => ReactElement;

const ROW =
  "flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring";

const FACT = "text-body-sm text-muted-foreground sm:w-40 sm:shrink-0";

const NEUTRAL_MESSAGE = "mt-6 text-body text-muted-foreground";

export const EMPTY_PATIENTS_MESSAGE =
  "No patients yet. Clients you have appointments with show up here.";

export const UNAVAILABLE_PATIENTS_MESSAGE =
  "Your patients list isn't available yet. Ask an admin to link your Healthie account.";

/** The Client's initials avatar: "row" on the list, "header" on their page. */
export function PatientAvatar({
  patient,
  size,
}: {
  patient: Pick<PatientListItem, "id" | "displayName" | "initials">;
  size: "row" | "header";
}): ReactElement {
  const label = patient.initials ?? initials(patientName(patient));

  return (
    <span
      aria-hidden="true"
      className={cn(
        "grid shrink-0 place-items-center rounded-full font-sans font-bold text-white",
        size === "row" ? "size-11 text-body" : "size-16 text-title-sm",
        avatarClass(patient.id)
      )}
    >
      {label}
    </span>
  );
}

function PatientRowBody({
  patient,
  timezone,
  now,
}: {
  patient: PatientListItem;
  timezone: string;
  now: Date;
}): ReactElement {
  return (
    <>
      <PatientAvatar patient={patient} size="row" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-6">
        <span className="min-w-0 flex-1 truncate text-body font-semibold text-foreground brand:text-title-sm brand:font-bold">
          {patientName(patient)}
        </span>
        <span
          className={FACT}
        >{`Last visit ${lastVisitLabel(patient, timezone, now)}`}</span>
        <span
          className={FACT}
        >{`Next visit ${nextVisitLabel(patient, timezone, now)}`}</span>
      </span>
    </>
  );
}

/** The list itself, or the plain message for an empty or unavailable list. */
export function PatientList({
  view,
  timezone,
  now,
  renderPatientLink,
}: {
  view: PatientListView;
  timezone: string;
  now: Date;
  renderPatientLink: RenderPatientLink;
}): ReactElement {
  if (view.kind === "empty") {
    return <p className={NEUTRAL_MESSAGE}>{EMPTY_PATIENTS_MESSAGE}</p>;
  }

  if (view.kind === "unavailable") {
    return <p className={NEUTRAL_MESSAGE}>{UNAVAILABLE_PATIENTS_MESSAGE}</p>;
  }

  return (
    <ul
      aria-label="Patients"
      className={cn(
        "mt-6 divide-y divide-border overflow-hidden rounded-xl border border-border bg-card",
        THEMED_CARD,
        THEMED_DIVIDE
      )}
    >
      {view.patients.map((patient) => (
        <li key={patient.id}>
          {renderPatientLink({
            patientId: patient.id,
            className: cn(ROW, THEMED_ROW_HOVER),
            dataAttributes: { "data-patient-id": patient.id },
            children: (
              <PatientRowBody patient={patient} timezone={timezone} now={now} />
            ),
          })}
        </li>
      ))}
    </ul>
  );
}

/** The page title, set in display type under the always theme. */
export function PatientsHeading(): ReactElement {
  return (
    <>
      <h1 className="font-sans text-3xl font-bold tracking-tight text-foreground brand:text-display brand:leading-[1.15]">
        Patients
      </h1>
      <p className="mt-2 text-body text-muted-foreground">
        Everyone you have appointments with, most recently seen first.
      </p>
    </>
  );
}
